import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import API_ENDPOINTS, { IMAGE_BASE_URL } from "../../api/apiConfig";
import type { SubCategory as SubCategoryItem } from "../../types";
import '../../assets/css/theme.css';
import '../../assets/css/shop.css';        // shared page-header band (.ffs-hero*) + empty/error state
import '../../assets/css/collections.css';

const SubCategory = () => {
  const { c_slug } = useParams();
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState<SubCategoryItem[]>([]);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const formatTitle = (slug = '') =>
    slug
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(API_ENDPOINTS.SUBCATEGORY, {
          method: 'POST',
          headers,
          body: JSON.stringify({ c_slug }),
        });
        const data = await response.json();

        if (response.ok && data.status && Array.isArray(data.data)) {
          setSubCategories(data.data);
        } else {
          setSubCategories([]);
          setError(data.message || 'Failed to fetch subcategories.');
        }
      } catch {
        setSubCategories([]);
        setError('An error occurred while fetching subcategories.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubCategories();
  }, [c_slug, navigate]);

  const title = formatTitle(c_slug);

  return (
    <main className="ffk-page">

      {/* ── HEADER (compact, same band as the Products page) ── */}
      <header className="ffs-hero">
        <div className="ffh-container ffs-hero-inner">
          <div>
            <nav className="ffs-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span className="is-current">{title}</span>
            </nav>
            <h1 className="ffs-hero-title">{title} <em>Collection</em></h1>
          </div>

          {!loading && !error && (
            <p className="ffs-hero-sub">
              {subCategories.length} exclusive{' '}
              {subCategories.length === 1 ? 'style' : 'styles'} await you
            </p>
          )}
        </div>
      </header>

      {/* ── GRID ── */}
      <section className="ffk-catalog">
        <div className="ffh-container">

          {error && <div className="ffk-error" role="alert">{error}</div>}

          {loading ? (
            <div className="ffk-grid" aria-busy="true" aria-label="Loading collection">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="ffk-skel">
                  <div className="ffk-skel-media" />
                  <div className="ffs-skel-line" />
                  <div className="ffs-skel-line ffs-skel-line--short" />
                </div>
              ))}
            </div>
          ) : subCategories.length > 0 ? (
            <div className="ffk-grid">
              {subCategories.map((subCat, idx) => (
                <Link
                  key={subCat.sc_id}
                  to={`/products/${subCat.sc_slug}`}
                  className="ffk-card"
                  style={{ animationDelay: `${Math.min(idx, 11) * 0.06}s` }}
                >
                  <div className="ffk-media">
                    <img
                      src={`${IMAGE_BASE_URL}${subCat.sc_image}`}
                      alt={subCat.sc_name}
                      loading="lazy"
                    />
                    <span className="ffk-index" aria-hidden="true">{String(idx + 1).padStart(2, '0')}</span>
                    <span className="ffk-go" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </div>

                  <div className="ffk-body">
                    <h3 className="ffk-name">{subCat.sc_name}</h3>
                    {subCat.product_count !== undefined && (
                      <p className="ffk-count">
                        {subCat.product_count} <span>pieces</span>
                      </p>
                    )}
                    <span className="ffk-link">Explore</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            !error && (
              <div className="ffs-state">
                <span className="ffs-state-icon" aria-hidden="true">∅</span>
                <p>No styles found in this collection.</p>
                <Link to="/products" className="ffh-btn">View all products</Link>
              </div>
            )
          )}
        </div>
      </section>
    </main>
  );
};

export default SubCategory;